import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, deleteDoc, doc, query, orderBy, addDoc } from 'firebase/firestore';
import { Edit, Trash2, Plus, RefreshCw } from 'lucide-react';
import './AdminPanel.css';

const BlogManager = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchPosts();
    }, []);

    const fetchPosts = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);
            const postsData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setPosts(postsData);
        } catch (error) {
            console.error("Error fetching posts: ", error);
            setPosts([]);
        } finally {
            setLoading(false);
        }
    };

    const seedBlogPosts = async () => {
        if (!window.confirm("Add 5 sample blog posts about AI & Offshore Staffing?")) return;
        setLoading(true);
        try {
            // Fetch existing authors
            const authorsSnapshot = await getDocs(collection(db, 'authors'));
            const authors = authorsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            if (authors.length === 0) {
                alert("No authors found. Please add authors in the Author Manager first to populate posts with real profiles.");
                setLoading(false);
                return;
            }

            const getRandomAuthor = () => {
                const randomAuthor = authors[Math.floor(Math.random() * authors.length)];
                return {
                    name: randomAuthor.name,
                    id: randomAuthor.id
                };
            };

            const samplePosts = [
                {
                    title: "How AI is Revolutionizing Offshore Staffing",
                    excerpt: "Artificial Intelligence is not replacing offshore teams; it's making them smarter, faster, and more efficient. Discover the key shifts in the BPO landscape.",
                    content: `<h2>The Shift from "Cheap" to "Smart"</h2>
<p>For decades, the primary value proposition of offshore staffing was cost arbitrage. Companies hired teams in the Philippines or India to save 70% on labor costs. While cost savings remain critical, a new driver has emerged: <strong>Efficiency</strong>.</p>
<p>Artificial Intelligence (AI) involves more than just automation. It's about augmentation. By equipping offshore talent with AI tools—like GitHub Copilot for developers or Jasper for content writers—businesses can achieve 2x to 3x the output effectively.</p>
<h3>Key Benefits of AI-Augmented Staffing</h3>
<ul>
<li><strong>Speed:</strong> Tasks that took hours now take minutes.</li>
<li><strong>Quality Assurance:</strong> AI agents can review code, proofread text, and audit calls in real-time.</li>
<li><strong>Skill Elevation:</strong> Junior staff can perform at a senior level with AI guidance.</li>
</ul>
<p>The future of BPO isn't just about finding lower-cost talent; it's about finding talent that knows how to leverage AI to deliver superior results.</p>`,
                    author: "",
                    authorId: "",
                    category: "Technology",
                    imageUrl: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?auto=format&fit=crop&q=80&w=1000",
                    published: true,
                    createdAt: new Date(),
                    readTime: "5 min read"
                },
                {
                    title: "Top 5 Benefits of AI-Augmented BPO",
                    excerpt: "From predictive analytics to automated quality assurance, learn how AI tools are driving unprecedented value in business process outsourcing.",
                    content: `<h2>Why Your BPO Strategy Needs an Upgrade</h2>
<p>Traditional BPO models are becoming obsolete. Manual data entry, scripted customer support, and reactive problem solving are being replaced by proactive, data-driven AI solutions.</p>
<h3>1. Predictive Analytics</h3>
<p>AI doesn't just report on what happened; it predicts what <em>will</em> happen. In customer support, this means anticipating volume spikes. In sales, it means scoring an entry based on their likelihood to buy.</p>
<h3>2. Automated Quality Assurance (QA)</h3>
<p>Instead of manually reviewing 1% of calls, AI can transcribe and analyze 100% of customer interactions, flagging compliance risks and sentiment trends instantly.</p>
<h3>3. 24/7 Availability without Burnout</h3>
<p>AI chatbots handle the Level 1 queries, allowing your human agents to focus on complex, high-value problem solving. This reduces burnout and improves job satisfaction.</p>
<h3>4. Hyper-Personalization</h3>
<p>AI enables mass personalization. Marketing teams can generate thousands of unique email variations, and support agents can pull up customer history and suggested next steps in milliseconds.</p>
<h3>5. Cost Efficiency at Scale</h3>
<p>While AI tools have a license cost, they reduce the headcount needed for repetitive tasks. You get a leaner, more agile team that produces more output.</p>`,
                    author: "",
                    authorId: "",
                    category: "Business Strategy",
                    imageUrl: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1000",
                    published: true,
                    createdAt: new Date(),
                    readTime: "4 min read"
                },
                {
                    title: "The Future of Work: Human + AI Collaboration",
                    excerpt: "Why the most successful companies are prioritizing a hybrid model that leverages human empathy with machine precision.",
                    content: `<h2>The Hybrid Advantage</h2>
<p>The narrative that "AI will replace humans" is fundamentally flawed. The reality is that <strong>Humans + AI</strong> will replace humans who don't use AI.</p>
<p>In the BPO sector, empathy, cultural nuance, and complex judgment are irreplaceable. AI cannot soothe an angry customer who feels unheard. AI cannot negotiate a delicate partnership deal.</p>
<h3>Where AI Excel vs. Where Humans Excel</h3>
<ul>
<li><strong>AI:</strong> Pattern recognition, data processing, speed, repetition, multilingual translation.</li>
<li><strong>Humans:</strong> Empathy, strategy, creative problem solving, ethics, leadership.</li>
</ul>
<p>The "Centaur Model"—a human expert riding an AI engine—is the gold standard for modern offshore teams.</p>`,
                    author: "",
                    authorId: "",
                    category: "Future of Work",
                    imageUrl: "https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&q=80&w=1000",
                    published: true,
                    createdAt: new Date(),
                    readTime: "6 min read"
                },
                {
                    title: "Mitigating Risks in Outsourcing with AI Tools",
                    excerpt: "Security and compliance are top concerns for outsourcing. See how AI-driven monitoring ensures data protection and regulatory adherence.",
                    content: `<h2>Security in the Age of Remote Work</h2>
<p>Data privacy is the #1 concern for CFOs when approving offshore contracts. How do you ensure a remote employee isn't downloading your database?</p>
<h3>AI-Driven Security Protocols</h3>
<p>Modern BPO providers use AI-driven endpoint detection and response (EDR) tools. These systems monitor behavior patterns to detect anomalies.</p>
<ul>
<li><strong>Auto-Redaction:</strong> AI can automatically blur credit card numbers (PCI DSS) or patient info (HIPAA) from screen recordings and transcripts.</li>
<li><strong>Biometric Authentication:</strong> Continuous facial recognition ensures the person behind the keyboard is the authorized employee.</li>
<li><strong>Phishing Detection:</strong> AI filters can catch sophisticated spear-phishing attacks before they reach your employee's inbox.</li>
</ul>
<p>AI makes outsourcing <em>safer</em>, not riskier, by provides continuous, automated vigilance.</p>`,
                    author: "",
                    authorId: "",
                    category: "Risk Management",
                    imageUrl: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=1000",
                    published: true,
                    createdAt: new Date(),
                    readTime: "5 min read"
                },
                {
                    title: "Cost Efficiency Analysis: Traditional vs. AI-Enhanced BPO",
                    excerpt: "A deep dive into the numbers. We analyze how AI integrators reduce long-term costs while improving output quality.",
                    content: `<h2>The ROI of AI Integration</h2>
<p>Let's look at the numbers. A traditional BPO seat costs $1,500 - $2,500 per month. An AI-Augmented seat might cost $2,000 - $3,000 due to tool licensing.</p>
<p><strong>However, the output changes effectively.</strong></p>
<p>A traditional agent closes 10 tickets per hour. An AI-augmented agent closes 18 tickets per hour. The effective cost per ticket drops by 40%.</p>
<h3>Hidden Savings</h3>
<ul>
<li><strong>Training Costs:</strong> AI assistants provide real-time coaching, reducing the ramp-up time for new hires by 50%.</li>
<li><strong>Error Correction:</strong> Automated QA catches errors before they impact the client, saving the cost of rework and refunds.</li>
<li><strong>Retention:</strong> Employees with better tools are less frustrated and stay longer, reducing recruitment costs.</li>
</ul>`,
                    author: "",
                    authorId: "",
                    category: "Finance",
                    imageUrl: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1000",
                    published: false, // Draft
                    createdAt: new Date(),
                    readTime: "7 min read"
                }
            ];

            for (const post of samplePosts) {
                const authorData = getRandomAuthor();
                post.author = authorData.name;
                post.authorId = authorData.id;
                await addDoc(collection(db, 'posts'), post);
            }
            alert("Sample blog posts added and linked to existing authors!");
            fetchPosts();
        } catch (error) {
            console.error("Error seeding posts:", error);
            alert("Failed to seed posts.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this post?')) {
            try {
                await deleteDoc(doc(db, 'posts', id));
                setPosts(posts.filter(post => post.id !== id));
            } catch (error) {
                console.error("Error deleting post: ", error);
                alert('Failed to delete post');
            }
        }
    };

    return (
        <div className="panel active" style={{ background: 'transparent' }}>
            <div className="page-hdr">
                <div className="page-hdr-left">
                    <div className="eyebrow">Content</div>
                    <h1 className="text-white">Blog Posts <span>.</span></h1>
                </div>
                <div className="page-hdr-actions">
                    <button className="btn btn-ghost" onClick={seedBlogPosts} style={{ borderColor: 'var(--border)', color: 'var(--text2)' }}>
                        <RefreshCw size={14} /> Seed Posts
                    </button>
                    <Link to="/admin/blog/new" className="btn btn-primary" style={{ backgroundColor: 'var(--accent)', color: '#000' }}>
                        <Plus size={14} style={{ marginRight: '4px' }} /> New Post
                    </Link>
                </div>
            </div>

            <div className="stats-grid">
                <div className="stat-card" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                    <div className="s-label">Total Posts</div>
                    <div className="s-val" style={{ color: 'var(--accent)' }}>{posts.length}</div>
                </div>
                <div className="stat-card green" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                    <div className="s-label">Published</div>
                    <div className="s-val" style={{ color: 'var(--success)' }}>{posts.filter(p => p.published).length}</div>
                </div>
                <div className="stat-card warn" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                    <div className="s-label">Drafts</div>
                    <div className="s-val" style={{ color: 'var(--warn)' }}>{posts.filter(p => !p.published).length}</div>
                </div>
            </div>

            <div className="tbl-wrap" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                {loading ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: 'var(--muted)' }}>Loading posts...</div>
                ) : posts.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center' }}>
                        <p style={{ color: 'var(--muted)', marginBottom: '10px' }}>No blog posts found.</p>
                        <Link to="/admin/blog/new" style={{ color: 'var(--accent)', fontSize: '13px' }}>Create your first post</Link>
                    </div>
                ) : (
                    <div className="tbl-scroll">
                        <table style={{ background: 'transparent' }}>
                            <thead>
                                <tr style={{ background: 'var(--surface2)', borderBottomColor: 'var(--border)' }}>
                                    <th style={{ color: 'var(--muted)' }}>Title</th>
                                    <th style={{ color: 'var(--muted)' }}>Status</th>
                                    <th style={{ color: 'var(--muted)' }}>Date</th>
                                    <th style={{ color: 'var(--muted)' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {posts.map((post) => (
                                    <tr key={post.id} style={{ borderBottomColor: 'var(--border)' }}>
                                        <td style={{ color: 'var(--text)' }}>
                                            <div style={{ fontWeight: 500 }}>
                                                <a
                                                    href={`/blog/${post.slug || post.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="hover:underline transition-all"
                                                    style={{ color: 'var(--text)', textDecoration: 'none' }}
                                                >
                                                    {post.title}
                                                </a>
                                            </div>
                                            <div style={{ fontSize: '10px', color: 'var(--muted)', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {post.excerpt || 'No excerpt'}
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`badge ${post.published ? 'badge-organic' : 'badge-bing'}`}>
                                                {post.published ? 'Published' : 'Draft'}
                                            </span>
                                        </td>
                                        <td style={{ color: 'var(--muted)', fontSize: '11px' }}>
                                            {post.createdAt?.seconds ? new Date(post.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                                        </td>
                                        <td>
                                            <div className="action-btns">
                                                <Link to={`/admin/blog/edit/${post.id}`} className="btn-ghost" style={{ padding: '4px', display: 'inline-flex', borderColor: 'var(--border)', color: 'var(--text2)' }}>
                                                    <Edit size={14} />
                                                </Link>
                                                <button onClick={() => handleDelete(post.id)} className="btn-ghost" style={{ padding: '4px', color: 'var(--accent3)', borderColor: 'var(--accent3-dim)' }}>
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BlogManager;
